// src/pages/dms/SCM.jsx
import React, { useState, useEffect } from "react";
import {
    Typography,
    Box,
    Breadcrumbs,
    Tabs,
    Tab,
    Paper,
    useMediaQuery,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    Link as MuiLink // Import Link from Material-UI for clickable breadcrumbs
} from "@mui/material";

import { useTheme } from "@mui/material/styles";
import SCMConfig from "../../components/SCMConfig"; // Import SCMConfig
import useAuthCheck from "../../hooks/useAuthCheck";
import { useOutletContext, useNavigate } from "react-router-dom";
import { database } from "../../firebase"; // Import instance 'database' dari firebase.js
import { ref, onValue, off } from "firebase/database"; // Import Firebase DB methods

// Define tab configurations outside the component to avoid unnecessary re-renders.
// This will define the display and data to be retrieved by SCMConfig
const tabConfigurations = [
    {
        label: "FLOW ORDER BARANG EXPAND",
        dbPath: "db_config/db_scm/tb_flow_order_barang_expand", // New DB location
        fieldName: "flow_name", // Field to be used as name in delete dialog
        primaryKeyField: "id_flow_config", // Unique key for each flow configuration

        // DEFINE FIELDS FOR ADD/EDIT APPROVAL FLOW FORM
        formFields: [
            { id: "flow_name", label: "Flow Name", type: "text", autoFocus: true },
            {
                // Changed id and label to refer to 'jabatan' (position)
                id: "created_by_jabatan_id",
                label: "Created By Position (ID)",
                type: "select",
                options: [] // Will be populated in useEffect
            },
            {
                id: "approval_steps",
                label: "Approval Steps (JSON)",
                type: "text",
                multiline: true,
                rows: 8,
                helperText: "Enter approval step configuration in JSON format. Example: {\"approved\":{\"order\":1,\"target_jabatan_ids\":[\"position1\",\"position2\"]}, ...}"
            },
            // Add other fields if your approval_flow_config has more
        ],

        // DEFINE HEADERS FOR THE TABLE
        tableHeaders: [
            { id: "flow_name", label: "Flow Name" },
            // Changed id to refer to 'jabatan' (position)
            { id: "created_by_jabatan_id", label: "Created By Position (ID)" },
            { id: "approval_steps", label: "Steps (JSON)" }, // To display raw JSON
            // Add other headers if any
        ],
    },
    // If there are other tabs for SCM in the future, add them here
];

function SCM() {
    const { user, loading: authLoading } = useAuthCheck(); // Using useAuthCheck
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const navigate = useNavigate();
    const { breadcrumbs } = useOutletContext(); // Get breadcrumbs from Layout

    const [tabIndex, setTabIndex] = useState(() => {
        // Get tabIndex from localStorage, default to 0
        const storedTabIndex = localStorage.getItem('scmTabIndex');
        const parsedIndex = parseInt(storedTabIndex, 10);
        if (!isNaN(parsedIndex) && parsedIndex >= 0 && parsedIndex < tabConfigurations.length) {
            return parsedIndex;
        }
        return 0; // Default to the first tab
    });

    // Save tabIndex to localStorage whenever it changes
    useEffect(() => {
        localStorage.setItem('scmTabIndex', tabIndex.toString());
    }, [tabIndex]);

    // Changed state from roles to jabatan (positions)
    const [loadingJabatan, setLoadingJabatan] = useState(true);
    const [jabatan, setJabatan] = useState([]); // State to store the list of jabatan

    // Get list of jabatan from Firebase to populate dropdown
    useEffect(() => {
        // Changed path to tb_jabatan
        const jabatanRef = ref(database, 'db_config/db_wfms/tb_jabatan');
        const unsubscribe = onValue(jabatanRef, (snapshot) => {
            const jabatanData = [];
            snapshot.forEach((childSnapshot) => {
                const jab = childSnapshot.val(); // Using 'jab' to avoid name conflicts
                // Assuming tb_jabatan has id_jabatan and nama_jabatan
                if (jab.id_jabatan && jab.nama_jabatan) {
                    jabatanData.push({ label: jab.nama_jabatan, value: jab.id_jabatan });
                }
            });
            setJabatan(jabatanData); // Using setJabatan
            setLoadingJabatan(false);
        }, (error) => {
            console.error("Error fetching jabatan:", error);
            setLoadingJabatan(false);
        });

        return () => off(jabatanRef, 'value', unsubscribe); // Cleanup listener
    }, []);

    // Update formFields options once jabatan are loaded
    useEffect(() => {
        if (!loadingJabatan) { // Using loadingJabatan
            const flowOrderExpandTab = tabConfigurations.find(tab => tab.label === "FLOW ORDER BARANG EXPAND");
            if (flowOrderExpandTab) {
                // Changed the id of the field being searched
                const createdByJabatanField = flowOrderExpandTab.formFields.find(field => field.id === "created_by_jabatan_id");
                if (createdByJabatanField) {
                    createdByJabatanField.options = jabatan; // Using jabatan state
                }
            }
        }
    }, [loadingJabatan, jabatan]); // Using loadingJabatan and jabatan as dependencies


    const handleTabChange = (event, newValue) => {
        setTabIndex(newValue);
    };

    const currentTabConfig = tabConfigurations[tabIndex];

    if (authLoading || loadingJabatan) { // Combine auth loading and jabatan loading
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) { // Check if user is logged in
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">
                    You must be logged in to access this page
                </Alert>
            </Box>
        );
    }


    return (
        <Box sx={{ pb: isMobile ? 8 : 0 }}> {/* Add padding bottom for mobile so it's not covered by bottom tabs */}
            {/* Breadcrumbs */}
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
                {breadcrumbs && breadcrumbs.map((crumb, index) => (
                    crumb.path ? (
                        <MuiLink
                            key={index}
                            underline="hover"
                            color="inherit"
                            onClick={() => navigate(crumb.path)}
                            sx={{ cursor: 'pointer' }}
                        >
                            {crumb.label}
                        </MuiLink>
                    ) : (
                        <Typography key={index} color="text.primary">
                            {crumb.label}
                        </Typography>
                    )
                ))}
            </Breadcrumbs>

            {/* Main Layout: Flex Container for Content and Sidebar */}
            <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 4 }}>
                {/* Main content (SCMConfig) */}
                <Box sx={{ flex: 1 }}>
                    <Card>
                        <CardContent>
                            {/* Render SCMConfig dynamically based on tabIndex */}
                            {currentTabConfig && (
                                <SCMConfig
                                    title={`List of ${currentTabConfig.label}`}
                                    dbPath={currentTabConfig.dbPath}
                                    fieldName={currentTabConfig.fieldName}
                                    primaryKeyField={currentTabConfig.primaryKeyField}
                                    formFields={currentTabConfig.formFields} // Still pass formFields and tableHeaders
                                    tableHeaders={currentTabConfig.tableHeaders}
                                />
                            )}
                        </CardContent>
                    </Card>
                </Box>

                {/* Desktop sidebar (Vertical Tabs) */}
                {!isMobile && (
                    <Paper
                        elevation={1}
                        sx={{
                            alignSelf: "flex-start", // Align Paper to the top of the flex container
                        }}
                    >
                        <Tabs
                            orientation="vertical"
                            value={tabIndex}
                            onChange={handleTabChange}
                            TabIndicatorProps={{
                                sx: {
                                    left: 0,
                                    right: "auto",
                                    width: "4px", // Indicator width
                                    bgcolor: "primary.main", // Indicator color
                                },
                            }}
                            sx={{
                                width: 350, // Sidebar width
                                borderLeft: 1, // Left border
                                borderColor: "divider",
                                ".MuiTab-root": {
                                    alignItems: "flex-start", // Align tab text to the left
                                    justifyContent: "flex-start",
                                    px: 2, // Horizontal padding
                                },
                            }}
                        >
                            {/* Loop through tab configurations for the sidebar */}
                            {tabConfigurations.map((tab, index) => (
                                <Tab key={index} label={tab.label} />
                            ))}
                        </Tabs>
                    </Paper>
                )}
            </Box>

            {/* Bottom Tabs (Mobile) */}
            {isMobile && (
                <Paper
                    sx={{
                        position: "fixed",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        borderTop: 1,
                        borderColor: "divider",
                        zIndex: 1000,
                    }}
                    elevation={3}
                >
                    <Tabs
                        value={tabIndex}
                        onChange={handleTabChange}
                        variant="scrollable"
                        scrollButtons
                        allowScrollButtonsMobile
                        TabIndicatorProps={{
                            sx: { bgcolor: "primary.main", height: 3 },
                        }}
                        centered={false}
                    >
                        {/* Loop through tab configurations for mobile */}
                        {tabConfigurations.map((tab, index) => (
                            <Tab key={index} label={tab.label} />
                        ))}
                    </Tabs>
                </Paper>
            )}
        </Box>
    );
}

export default SCM;